import {Children, FC, ReactNode, isValidElement, useEffect} from 'react'
import clsx from 'clsx'
import {useLocation} from 'react-router'
import {WithChildren} from '_metronic/helpers'
import {useLang} from '_metronic/i18n/Metronici18n'
import {checkIsActive} from 'utility/checkvalidUrl'
import {usePermissions} from '@services/helper/permission.helper'
import {useSidebarGroups} from './SidebarGroups'

const titleOf = (node: ReactNode) =>
  isValidElement<{title?: string}>(node) ? node.props.title ?? '' : ''

type Props = {
  /** The group's key: its remembered open/closed state is stored under it. */
  to: string
  title: string
  fontIcon?: string
  /**
   * The child feature urls. The group renders only when the user is granted at least one of
   * them — a group whose children are all gated away must not render as an empty accordion.
   */
  anyOf: string[]
  /**
   * The urls that make the group active, when they are not its `anyOf` feature urls (a screen
   * with no feature row of its own). Defaults to `anyOf`.
   */
  childUrls?: string[]
  /** Shown open until the viewer folds it. Every other group starts folded. */
  defaultOpen?: boolean
}

const SidebarMenuItemWithSub: FC<Props & WithChildren> = ({
  children,
  to,
  title,
  fontIcon,
  anyOf,
  childUrls,
  defaultOpen = false,
}) => {
  const {pathname} = useLocation()
  const lang = useLang()
  const {hasAny} = usePermissions()
  const {isOpen, setOpen, reveal} = useSidebarGroups()
  // Active when the current page is one of the group's screens. itam's groups do not follow the url
  // prefixes (Assets is /asset but sits in Master Data), so the group's own `to`
  // (only its open/closed key) cannot tell them apart.
  const isActive =
    checkIsActive(pathname, to) || (childUrls || anyOf).some((url) => checkIsActive(pathname, url))
  const expanded = isOpen(to, defaultOpen)

  // Arriving on a page inside a folded group unfolds it, so the current page is never hidden.
  // It fires on arrival only, so the group can still be folded while one of its pages is open.
  useEffect(() => {
    if (isActive) reveal(to)
  }, [isActive, reveal, to])

  if (!hasAny(anyOf)) return null

  // A group's pages are listed A–Z by the label the viewer sees, so the order follows the language.
  const pages = Children.toArray(children).sort((a, b) =>
    titleOf(a).localeCompare(titleOf(b), lang, {sensitivity: 'base', numeric: true})
  )

  // No data-kt-menu-trigger: the open/closed state is React's (SidebarGroups), not Metronic's.
  return (
    <div className={clsx('menu-item menu-accordion', {here: isActive, show: expanded})}>
      <span
        className='menu-link'
        role='button'
        aria-expanded={expanded}
        onClick={() => setOpen(to, !expanded)}
      >
        {fontIcon && (
          <span className='menu-icon'>
            <i className={clsx('bi fs-3', fontIcon)}></i>
          </span>
        )}
        <span className='menu-title'>{title}</span>
        <span className='menu-arrow'></span>
      </span>
      <div className='menu-sub menu-sub-accordion'>{pages}</div>
    </div>
  )
}

export {SidebarMenuItemWithSub}
